import React, { useEffect, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, DatePicker, Input } from '@/shared/ui'
import { useClientTariff, useLegalEntities, useRegions } from '@/shared/hooks'
import type { ClientTariffRuleWrite, ClientTariffWriteRequest } from '@/shared/api'

export const KIND_LABELS: Record<number, string> = { 1: 'Общий', 2: 'Специальный' }
export const RULE_PARAM_LABELS: Record<number, string> = {
  1: 'Тип доставки',
  2: 'Интервал',
  3: 'Точка отправления',
}
export const RULE_OPERATOR_LABELS: Record<number, string> = {
  0: 'Равно',
  1: 'Больше или равно',
  2: 'Меньше или равно',
}

const EMPTY_RULE: ClientTariffRuleWrite = {
  param: 1,
  operator: 0,
  value: '',
  label: '',
  amount: 0,
  enabled: true,
}

interface ClientTariffFormModalProps {
  isOpen: boolean
  tariffId?: number | null
  readOnly?: boolean
  onClose: () => void
  onSubmit: (body: ClientTariffWriteRequest) => void | Promise<void>
  isLoading?: boolean
}

export const ClientTariffFormModal: React.FC<ClientTariffFormModalProps> = ({
  isOpen,
  tariffId,
  readOnly = false,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const detailId = tariffId ?? 0
  const isEdit = detailId > 0

  const { data: tariff } = useClientTariff(detailId)
  const { data: regions } = useRegions(true)
  const { data: companies } = useLegalEntities({ page: 1, limit: 100, status: 1 })

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [kind, setKind] = useState<number>(1)
  const [regionId, setRegionId] = useState<number | ''>('')
  const [legalEntityId, setLegalEntityId] = useState<number | ''>('')
  const [dateFrom, setDateFrom] = useState<Date | null>(null)
  const [dateTo, setDateTo] = useState<Date | null>(null)
  const [basePrice, setBasePrice] = useState<number>(0)
  const [rules, setRules] = useState<ClientTariffRuleWrite[]>([])
  const [error, setError] = useState<string | null>(null)

  // Заполняем форму один раз на открытие, чтобы фоновый рефетч не сбрасывал правки.
  const populatedRef = useRef(false)

  useEffect(() => {
    if (!isOpen) {
      populatedRef.current = false
      return
    }
    if (populatedRef.current) return
    setError(null)
    if (isEdit) {
      if (!tariff) return
      populatedRef.current = true
      setName(tariff.name)
      setDescription(tariff.description ?? '')
      setKind(tariff.kind)
      setRegionId(tariff.region_id)
      setLegalEntityId(tariff.legal_entity_id ?? '')
      setDateFrom(tariff.date_from ? new Date(tariff.date_from * 1000) : null)
      setDateTo(tariff.date_to ? new Date(tariff.date_to * 1000) : null)
      setBasePrice(Number(tariff.base_price))
      setRules(
        tariff.rules.map((rule) => ({
          param: rule.param,
          operator: rule.operator,
          value: rule.value,
          label: rule.label,
          amount: Math.trunc(Number(rule.amount)),
          enabled: rule.enabled,
        }))
      )
      return
    }
    populatedRef.current = true
    setName('')
    setDescription('')
    setKind(1)
    setRegionId('')
    setLegalEntityId('')
    setDateFrom(null)
    setDateTo(null)
    setBasePrice(0)
    setRules([])
  }, [isOpen, isEdit, tariff])

  if (!isOpen) return null

  const setRule = (index: number, patch: Partial<ClientTariffRuleWrite>) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)))
  }

  const handleSubmit = () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Укажите название тарифа')
      return
    }
    if (regionId === '') {
      setError('Выберите город')
      return
    }
    if (kind === 2 && legalEntityId === '') {
      setError('Для специального тарифа выберите компанию')
      return
    }
    if (rules.some((rule) => !rule.value.trim() || !rule.label.trim())) {
      setError('Заполните значение и подпись у всех правил')
      return
    }

    const body: ClientTariffWriteRequest = {
      name: trimmedName,
      description: description.trim() ? description.trim() : null,
      kind,
      regionId: Number(regionId),
      legalEntityId: kind === 2 ? Number(legalEntityId) : null,
      dateFrom: dateFrom ? Math.floor(dateFrom.getTime() / 1000) : null,
      dateTo: dateTo ? Math.floor(dateTo.getTime() / 1000) : null,
      basePrice,
      rules: rules.map((rule) => ({
        ...rule,
        value: rule.value.trim(),
        label: rule.label.trim(),
      })),
    }
    void onSubmit(body)
  }

  const disabled = readOnly || isLoading

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {readOnly ? 'Тариф' : isEdit ? 'Редактировать тариф' : 'Новый тариф'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <Input
            label="Название"
            value={name}
            disabled={disabled}
            placeholder="Например, Городской тариф"
            onChange={(e) => setName(e.target.value)}
          />

          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Описание</label>
            <textarea
              value={description}
              disabled={disabled}
              rows={3}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500"
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Тип</label>
              <select
                className="admin-input"
                value={kind}
                disabled={disabled}
                onChange={(e) => setKind(Number(e.target.value))}
              >
                {Object.entries(KIND_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Город</label>
              <select
                className="admin-input"
                value={regionId}
                disabled={disabled}
                onChange={(e) => setRegionId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— выберите город —</option>
                {(regions ?? []).map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>

            {kind === 2 && (
              <div className="space-y-1 sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Компания</label>
                <select
                  className="admin-input"
                  value={legalEntityId}
                  disabled={disabled}
                  onChange={(e) =>
                    setLegalEntityId(e.target.value ? Number(e.target.value) : '')
                  }
                >
                  <option value="">— выберите компанию —</option>
                  {(companies?.items ?? []).map((company) => (
                    <option key={company.legalEntityId} value={company.legalEntityId}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Действует с</label>
              <DatePicker value={dateFrom} disabled={disabled} onChange={setDateFrom} />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Действует по</label>
              <DatePicker value={dateTo} disabled={disabled} onChange={setDateTo} />
            </div>

            <div className="space-y-1 sm:col-span-2">
              <Input
                label="Базовая стоимость, ₽"
                type="number"
                value={basePrice}
                disabled={disabled}
                onChange={(e) => setBasePrice(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-900">Правила-надбавки</h3>
              {!readOnly && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isLoading}
                  onClick={() => setRules((prev) => [...prev, { ...EMPTY_RULE }])}
                >
                  Добавить правило
                </Button>
              )}
            </div>

            {rules.length === 0 && (
              <div className="text-sm text-gray-500">Правила не добавлены</div>
            )}

            {rules.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-xs font-medium uppercase text-gray-400">
                  <div className="col-span-2">Параметр</div>
                  <div className="col-span-2">Оператор</div>
                  <div className="col-span-2">Значение</div>
                  <div className="col-span-2">Подпись</div>
                  <div className="col-span-2">Сумма, ₽</div>
                  <div className="col-span-1">Вкл.</div>
                  <div className="col-span-1" />
                </div>
                {rules.map((rule, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center">
                    <select
                      className="admin-input col-span-2"
                      value={rule.param}
                      disabled={disabled}
                      onChange={(e) => setRule(index, { param: Number(e.target.value) })}
                    >
                      {Object.entries(RULE_PARAM_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <select
                      className="admin-input col-span-2"
                      value={rule.operator}
                      disabled={disabled}
                      onChange={(e) => setRule(index, { operator: Number(e.target.value) })}
                    >
                      {Object.entries(RULE_OPERATOR_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <input
                      className="admin-input col-span-2"
                      placeholder="Значение"
                      value={rule.value}
                      disabled={disabled}
                      onChange={(e) => setRule(index, { value: e.target.value })}
                    />
                    <input
                      className="admin-input col-span-2"
                      placeholder="Подпись"
                      value={rule.label}
                      disabled={disabled}
                      onChange={(e) => setRule(index, { label: e.target.value })}
                    />
                    <input
                      className="admin-input col-span-2"
                      type="number"
                      min={0}
                      step={1}
                      value={rule.amount}
                      disabled={disabled}
                      onChange={(e) =>
                        setRule(index, {
                          amount: e.target.value === ''
                            ? 0
                            : Math.trunc(Number(e.target.value)),
                        })
                      }
                    />
                    <label className="col-span-1 flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={rule.enabled}
                        disabled={disabled}
                        onChange={(e) => setRule(index, { enabled: e.target.checked })}
                      />
                    </label>
                    <div className="col-span-1">
                      {!readOnly && (
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={isLoading}
                          onClick={() =>
                            setRules((prev) => prev.filter((_, i) => i !== index))
                          }
                        >
                          Удалить
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && <div className="text-sm text-error-600">{error}</div>}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            {readOnly ? 'Закрыть' : 'Отмена'}
          </Button>
          {!readOnly && (
            <Button onClick={handleSubmit} isLoading={isLoading}>
              {isEdit ? 'Сохранить' : 'Создать'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
